/**
 * useEntitlements — the organization's resolved feature map.
 *
 * Reads `GET /api/entitlements/my`, which already resolves the whole
 * precedence chain server-side (org override -> league override -> the
 * governing tier -> registry default). The client never recomputes any of
 * that, and deliberately keeps NO list of feature keys: BackOfficePro restates
 * its key list three times inside one hook, so a feature added to its registry
 * never reaches the client without three separate edits (defect D4). Here the
 * map is whatever the API returns.
 *
 * FAILS OPEN. While loading, on an error, or for a response without a feature
 * map, every feature reads as `on`. This is a visibility layer, not an
 * authorization boundary — each route keeps its own role checks — so a flaky
 * request must never blank an operator's navigation. It is the same fail-safe
 * shape as the OL-managed header nav.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Semprion may change a tier or an override while a customer is signed in.
const POLL_MS = 60000;

export function useEntitlements() {
  const [state, setState] = useState({
    features: null,          // null = not known yet -> treat everything as on
    sources: {},
    categoryGates: {},       // nav category -> feature key, supplied by the API
    tier: null,
    tierLabel: null,
    saleModel: null,
    unmanaged: true,
    orgId: null,
    loaded: false,
    error: null,
  });
  const fetched = useRef(false);

  const load = useCallback(async (silent = false) => {
    try {
      // OL is cookie-authenticated; AuthContext sets axios.defaults.withCredentials
      // globally, so no Authorization header is involved here.
      const r = await axios.get(`${API_URL}/api/entitlements/my`);
      const d = r.data || {};
      setState({
        features: d.features || null,
        sources: d.sources || {},
        categoryGates: d.category_gates || {},
        tier: d.tier || null,
        tierLabel: d.tier_label || null,
        saleModel: d.sale_model || null,
        unmanaged: d.unmanaged !== false,
        orgId: d.acting_org_id || d.org_id || null,
        loaded: true,
        error: null,
      });
    } catch (err) {
      if (!silent) {
        // Worth one console line: an operator reporting "my tabs vanished"
        // should be traceable to this, not to a mystery.
        console.warn('[entitlements] could not load feature map; failing open', err?.message);
      }
      setState((p) => ({ ...p, loaded: true, error: 'unavailable' }));
    }
  }, []);

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      load();
    }
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(true), POLL_MS);
    const onFocus = () => { if (document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', onFocus);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onFocus); };
  }, [load]);

  const api = useMemo(() => {
    const { features, categoryGates } = state;

    /** 'off' | 'restrict' | 'on'. Unknown or not-yet-loaded reads as 'on'. */
    const stateOf = (key) => {
      if (!key) return 'on';                 // no key = ungated
      if (!features) return 'on';            // fail open
      return features[key] || 'off';
    };

    return {
      ...state,
      stateOf,
      /** Appears in navigation at all. `restrict` counts as visible-but-greyed. */
      isVisible: (key) => stateOf(key) !== 'off',
      /** Fully usable. `restrict` is NOT enabled — it renders a restricted notice. */
      isEnabled: (key) => stateOf(key) === 'on',
      isRestricted: (key) => stateOf(key) === 'restrict',

      /**
       * The feature key that gates a page row: its own `featureKey` when it has
       * one, otherwise its category's gate. A category with no gate is ungated.
       */
      gateFor: (page) => (page?.featureKey || categoryGates[page?.category] || null),

      reload: () => load(false),
    };
  }, [state, load]);

  return api;
}

export default useEntitlements;
